import React, { useState, useRef, useId } from 'react';
import { Card, Button, Spinner } from 'react-bootstrap';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faChevronDown } from '@fortawesome/free-solid-svg-icons';
import { currencyFormat } from 'helpers/utils';

/**
 * TransactionSummaryCard - A fully independent card component for displaying transaction metrics
 * with expandable calculation details. Each card maintains its own internal state.
 */
const TransactionSummaryCard: React.FC<{
  title: string;
  amount: number;
  calculation?: React.ReactNode;
  icon: any;
  iconColorClass: string;
  iconBgClass: string;
  valueColorClass: string;
  ariaLabel: string;
  hasCalculation: boolean;
  loading?: boolean;
}> = ({
  title,
  amount,
  calculation,
  icon,
  iconColorClass,
  iconBgClass,
  valueColorClass,
  ariaLabel,
  hasCalculation,
  loading = false
}) => {
  // Internal state for expanded/collapsed state - each card instance has its own state
  const [isExpanded, setIsExpanded] = useState(false);
  
  // Generate a truly unique ID for this specific card instance
  const uniqueIdBase = useId();
  // Use the title to create a readable ID prefix but ensure uniqueness
  const uniqueId = useRef(`${title.toLowerCase().replace(/\s+/g, '-')}-${uniqueIdBase.replace(/:/g, '-')}`);
  
  // Toggle function that only affects this specific card
  const onToggleExpand = () => {
    setIsExpanded(prev => !prev);
  };
  
  return (
    <div 
      className={`border border-opacity-25 rounded shadow-xl bg-body transaction-summary-card ${isExpanded ? 'expanded' : 'collapsed'}`}
      style={{
        transition: 'all 0.3s ease-in-out', 
        borderRadius: '0.5rem',
        width: '100%', // Fixed width - no expansion to the right
        overflow: 'hidden',
        position: 'relative',
        zIndex: isExpanded ? 10 : 1,
        boxShadow: isExpanded 
          ? 'var(--bs-box-shadow-lg, 0 4px 12px rgba(0, 0, 0, 0.15))' 
          : 'var(--bs-box-shadow-sm, 0 2px 6px rgba(0, 0, 0, 0.1))',
        transformOrigin: 'top', // Ensure expansion happens downward
      }}
    >
      <Card.Body className="p-4 bg-body">
        {/* Card header with title and icon */}
        <div className="d-flex justify-content-between align-items-center mb-3">
          <h6 className="mb-0 fs-8 fw-semibold text-body">{title}</h6>
          <div className={`d-flex align-items-center justify-content-center ${iconBgClass} rounded-circle`} 
               style={{ width: '38px', height: '38px' }}>
            <FontAwesomeIcon icon={icon} className={`${iconColorClass} fs-8`} />
          </div>
        </div>
        
        {/* Main value with expand button for cards with calculations */}
        <div className="d-flex justify-content-between align-items-center">
          {loading ? (
            <div className="d-flex align-items-center justify-content-center w-100">
              <Spinner 
                animation="border" 
                size="sm" 
                className={`${valueColorClass}`} 
                role="status"
                style={{ width: '1.5rem', height: '1.5rem' }}
              >
                <span className="visually-hidden">Loading...</span>
              </Spinner>
            </div>
          ) : (
            <h3 className={`fw-bold fs-7 ${valueColorClass} mb-0`} aria-label={ariaLabel}>
              <span className="currency-value">{currencyFormat(amount)}</span>
            </h3>
          )}
          
          {hasCalculation && (
            <Button 
              variant="link" 
              className={`p-0 border-0 ${valueColorClass} opacity-75 toggle-button`}
              onClick={onToggleExpand}
              aria-label={isExpanded ? "Hide calculation details" : "Show calculation details"}
              aria-expanded={isExpanded}
              aria-controls={uniqueId.current}
              style={{ 
                boxShadow: 'none',
                transition: 'transform 0.2s ease-in-out'
              }}
              tabIndex={0}
              onMouseEnter={(e) => {
                e.currentTarget.style.transform = 'scale(1.5)';
                const singleArrow = e.currentTarget.querySelector('.single-arrow') as HTMLElement;
                const doubleArrows = e.currentTarget.querySelector('.double-arrows') as HTMLElement;
                
                if (singleArrow && doubleArrows) {
                  singleArrow.style.opacity = '0';
                  doubleArrows.style.opacity = '1';
                  doubleArrows.style.transform = 'translateY(2px)';
                }
              }}
              onMouseLeave={(e) => {
                e.currentTarget.style.transform = 'scale(1)';
                const singleArrow = e.currentTarget.querySelector('.single-arrow') as HTMLElement;
                const doubleArrows = e.currentTarget.querySelector('.double-arrows') as HTMLElement;
                
                if (singleArrow && doubleArrows) {
                  singleArrow.style.opacity = '1';
                  doubleArrows.style.opacity = '0';
                  doubleArrows.style.transform = 'translateY(0)';
                }
              }}
              onFocus={(e) => {
                e.currentTarget.style.transform = 'scale(1.5)';
                const singleArrow = e.currentTarget.querySelector('.single-arrow') as HTMLElement;
                const doubleArrows = e.currentTarget.querySelector('.double-arrows') as HTMLElement;
                
                if (singleArrow && doubleArrows) {
                  singleArrow.style.opacity = '0';
                  doubleArrows.style.opacity = '1';
                  doubleArrows.style.transform = 'translateY(2px)';
                }
              }}
              onBlur={(e) => {
                e.currentTarget.style.transform = 'scale(1)';
                const singleArrow = e.currentTarget.querySelector('.single-arrow') as HTMLElement;
                const doubleArrows = e.currentTarget.querySelector('.double-arrows') as HTMLElement;
                
                if (singleArrow && doubleArrows) {
                  singleArrow.style.opacity = '1';
                  doubleArrows.style.opacity = '0';
                  doubleArrows.style.transform = 'translateY(0)';
                }
              }}
            >
              <div className="position-relative d-inline-block ms-2" style={{ width: '20px', height: '20px' }}>
                {/* Single arrow shown by default */}
                <div 
                  className="single-arrow position-absolute top-0 start-0" 
                  style={{ 
                    transition: 'opacity 0.2s ease-in-out',
                    opacity: 1
                  }}
                >
                  <FontAwesomeIcon 
                    icon={faChevronDown} 
                    className="fs-9"
                  />
                </div>
                
                {/* Double arrows shown on hover/focus */}
                <div 
                  className="double-arrows position-absolute top-0 start-0 d-flex flex-column align-items-center" 
                  style={{ 
                    transition: 'transform 0.2s ease-in-out, opacity 0.2s ease-in-out',
                    opacity: 0
                  }}
                >
                  <FontAwesomeIcon 
                    icon={faChevronDown} 
                    className="fs-10 mb-n1"
                  />
                  <FontAwesomeIcon 
                    icon={faChevronDown} 
                    className="fs-10 mt-n1"
                  />
                </div>
              </div>
            </Button>
          )}
        </div>
        
        {/* Optional calculation subtitle with pure height-based animation */}
        <div 
          id={uniqueId.current}
          className="calculation-container overflow-hidden"
          style={{
            maxHeight: isExpanded ? '500px' : '0', // Use maxHeight for smooth animation
            transition: 'all 0.3s ease-in-out', // Pure ease-in-out transition
            opacity: isExpanded ? 1 : 0,
            marginTop: isExpanded ? '12px' : '0',
            paddingTop: isExpanded ? '0' : '0',
            paddingBottom: isExpanded ? '0' : '0',
          }}
        >
          {calculation}
        </div>
      </Card.Body>
    </div>
  );
};

export default TransactionSummaryCard;
